import * as firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import mime from "mime-types";
import constants from "../constants";
import { getFileMetadata } from "../utils/fileInspection";
import Parking from '../models/parking.model';

if (firebase.apps && firebase.apps.length === 0) {
  firebase.initializeApp(constants.firebaseConfig);
}

export default class ParkingService {
  /**
   * Returns a Promise that resolves when a new parking has been created
   * in the subcollection of the corresponding community's profile. The
   * Promise resolves to a Profile.
   *
   * The data object must contain at least the following params:
   *
   * * `spotNumber`: the number of the parking spot
   * * `ownershipCertificate`: a file containing the document that proves the ownership
   *    of the parking spot
   * * `picture`: A File containing a picture of the parking spot
   *
   * @param {{
   * spotNumber: number
   * ownershipCertificate: File,
   * picture: File
   * }} data Object with the parking information
   * @returns resolves to a Parking object
   */
  static async createParking(data) {
    const creator = firebase.auth().currentUser;
    if (creator) {
      const db = firebase.firestore();
      const { picture, ownershipCertificate, ...others } = data;
      let parking = {
        type: "private",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdBy: db
          .collection(constants.collections.profiles)
          .doc(creator.uid),
        community: undefined,
        schedule: "default",
        available: true,
        ...others
      };
      const ownerSnapshot = await parking.createdBy.get();
      const owner = ownerSnapshot.exists ? ownerSnapshot.data() : undefined;
      if (owner && owner.role !== constants.roles.PARTNER) {
        throw new Error(
          "Cannot create parking if there's no partner user signed in."
        );
      }
      const communitySnapShot = await owner.createdBy.get();
      const community = communitySnapShot.exists
        ? communitySnapShot.data()
        : undefined;
      if (community && community.d.role !== constants.roles.COMMUNITY) {
        throw new Error("Owner must belong to a community");
      }
      parking.community = owner.createdBy;
      const pictureMetaData = await getFileMetadata(picture);
      const certificateMetaData = await getFileMetadata(ownershipCertificate);
      if (
        pictureMetaData.binaryFileType &&
        certificateMetaData.binaryFileType
      ) {
        const storageRef = firebase.storage().ref();
        const picturePath = `partners/${
          ownerSnapshot.id
        }/parkings/${parking.spotNumber}/picture/picture.${mime.extension(pictureMetaData.binaryFileType)}`;
        const certificatePath = `partners/${
          ownerSnapshot.id
        }/parkings/${parking.spotNumber}/certificate/ownershipCertificate.${mime.extension(
          certificateMetaData.binaryFileType
        )}`;
        parking.picture = picturePath;
        parking.ownershipCertificate = certificatePath;
        const certificateRef = storageRef.child(certificatePath);
        const pictureRef = storageRef.child(picturePath);
        const certificateSnapshot = certificateRef.put(
          ownershipCertificate,
          {
            contentType: certificateMetaData.binaryFileType,
            owner: `${ownerSnapshot.id}/${parking.spotNumber}`
          }
        );
        const pictureSnapshot = pictureRef.put(picture, {
          contentType: pictureMetaData.binaryFileType,
          owner: `${ownerSnapshot.id}/${parking.spotNumber}`
        });
        const parkingRef = db
          .collection(constants.collections.profiles)
          .doc(communitySnapShot.id)
          .collection(constants.collections.parkings)
          .add(parking);
        await certificateSnapshot;
        await pictureSnapshot;
        let hydration = {
          type: parking.type,
          raw: await parkingRef,
          createdAt: parking.createdAt,
          communityRef: parking.community,
          ownerRef: parking.createdBy,
          ownershipCertificatePath: certificatePath,
          picturePath: picturePath,
          schedule: parking.schedule,
          available: parking.available,
          spotNumber: parking.spotNumber,
          certificateURL: await certificateRef.getDownloadURL(),
          pictureURL: await pictureRef.getDownloadURL()
        };
        return new Parking(hydration);
      } else {
        throw new Error(
          `${
            pictureMetaData.binaryFileType ? "Picture" : "Ownership Certificate"
          } mime-type could not be recognized`
        );
      }
    } else {
      throw new Error(
        "Cannot creat parking if there's no partner user signed in."
      );
    }
  }

  static async createCommunityParking(data) {
    const creator = firebase.auth().currentUser;
    if (creator) {
      const db = firebase.firestore();
      const { picture, ...others } = data;
      let parking = {
        type: "community",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        createdBy: db
          .collection(constants.collections.profiles)
          .doc(creator.uid),
        community: undefined,
        schedule: "default",
        available: true,
        ...others
      };
      const ownerSnapshot = await parking.createdBy.get();
      const owner = ownerSnapshot.exists ? ownerSnapshot.data().d : undefined;
      if (owner && owner.role !== constants.roles.COMMUNITY) {
        throw new Error(
          "Role: Cannot create parking if there's no community user signed in."
        );
      }
      const communitySnapShot = ownerSnapshot;
      parking.community = db.collection(constants.collections.profiles).doc(ownerSnapshot.id);
      const pictureMetaData = await getFileMetadata(picture);
      if (pictureMetaData.binaryFileType) {
        const storageRef = firebase.storage().ref();
        const picturePath = `communities/${
          ownerSnapshot.id
        }/parkings/${parking.spotNumber}/picture/picture.${mime.extension(pictureMetaData.binaryFileType)}`;
        parking.picture = picturePath;
        const pictureRef = storageRef.child(picturePath);
        const pictureSnapshot = pictureRef.put(picture, {
          contentType: pictureMetaData.binaryFileType,
          owner: `${ownerSnapshot.id}/${parking.spotNumber}`
        });
        const parkingRef = db
          .collection(constants.collections.profiles)
          .doc(communitySnapShot.id)
          .collection(constants.collections.parkings)
          .add(parking);
        await pictureSnapshot;
        let hydration = {
          type: parking.type,
          raw: await parkingRef,
          createdAt: parking.createdAt,
          communityRef: parking.community,
          ownerRef: parking.createdBy,
          picturePath: picturePath,
          schedule: parking.schedule,
          available: parking.available,
          spotNumber: parking.spotNumber,
          pictureURL: await pictureRef.getDownloadURL()
        };
        return new Parking(hydration);
      } else {
        throw new Error(
          `${
            pictureMetaData.binaryFileType ? "Picture" : "Ownership Certificate"
          } mime-type could not be recognized`
        );
      }
    } else {
      throw new Error(
        "Creator: Cannot create parking if there's no community user signed in."
      );
    }
  }
}
